import { Col, Row } from 'react-bootstrap';
import PratusSelect, {
  OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import {
  useGetLatestIncidentWorkLogForUser,
  WORK_LOG_ACTIONS,
  useCreateIncidentMemberSession,
  useUpdateIncidentMemberSession,
} from 'hooks/incident/useWorkLog';
import {
  useMarkIncidentUserActive,
  useMarkIncidentUserInactive,
} from 'hooks/incident/useRoster';
import { useActivationActions } from 'hooks/incident/useActivationActions';
import {
  useUserGuid,
  useGroupGuid,
  useActualIncidentId,
} from '../hooks/commonHooks';
import React, { useMemo } from 'react';
import { toast } from 'react-toastify';

const activationStatusOptions = [
  { label: 'Activated', value: 'activated' },
  { label: 'Deactivated', value: 'deactivated' },
];

const checkInStatusOptions = [
  { label: 'Checked In', value: 'checked_in' },
  { label: 'Demobilized', value: 'checked_out' },
];

const signInStatusOptions = [
  { label: 'Signed In', value: 'signed_in' },
  { label: 'Not Signed In', value: 'signed_out' },
];

const MemberStatusControls: React.FC = () => {
  const incidentId = useActualIncidentId();
  const userGuid = useUserGuid();
  const groupGuid = useGroupGuid();

  const { data: userWorkLog } = useGetLatestIncidentWorkLogForUser(
    incidentId,
    userGuid
  );

  const markActive = useMarkIncidentUserActive(incidentId, groupGuid);
  const markInactive = useMarkIncidentUserInactive(incidentId, groupGuid);
  const { mutateAsync: handleActivationUpdate } = useActivationActions();
  const createSession = useCreateIncidentMemberSession();
  const updateSession = useUpdateIncidentMemberSession();

  const currentSession = userWorkLog?.sessions.length
    ? userWorkLog.sessions[0]
    : undefined;

  const activationStatus = useMemo(
    () =>
      activationStatusOptions.find(
        (opt) => opt.value === userWorkLog?.activationStatus
      ) || activationStatusOptions[1],
    [userWorkLog?.activationStatus]
  );

  const checkInStatus = useMemo(
    () =>
      checkInStatusOptions.find(
        (opt) => opt.value === userWorkLog?.checkInStatus
      ) || checkInStatusOptions[1],
    [userWorkLog?.checkInStatus]
  );

  const signInStatus = useMemo(
    () =>
      signInStatusOptions.find(
        (opt) => opt.value === userWorkLog?.signInStatus
      ) || signInStatusOptions[1],
    [userWorkLog?.signInStatus]
  );

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleString();
  };

  const handleActivationStatusChange = (value: unknown) => {
    const selectedValue = (value as OptionType).value;
    handleActivationUpdate({
      incidentId: incidentId,
      memberUserGuid: userGuid,
      isActivated: selectedValue === 'activated',
    });
  };

  const handleCheckInStatusChange = (value: unknown) => {
    const selectedValue = (value as OptionType)?.value;
    if (selectedValue === 'checked_in' && userWorkLog?.incidentMemberId) {
      markActive.mutate({
        user_guid: userGuid,
        incident_member_id: userWorkLog.incidentMemberId,
      });
    } else if (
      selectedValue === 'checked_out' &&
      userWorkLog?.loginTime &&
      userWorkLog?.incidentMemberId &&
      userWorkLog?.workLogId
    ) {
      // Prevent checking out when signed in
      if (currentSession?.signInTime && !currentSession?.signOutTime) {
        toast.error('You must sign out before checking out');
        return;
      }
      const loginTime = new Date(userWorkLog.loginTime);
      const logoutTime = new Date();
      const minutesWorked = Math.floor(
        (logoutTime.getTime() - loginTime.getTime()) / 60000
      );
      markInactive.mutate({
        user_guid: userGuid,
        logout_time: logoutTime,
        minutes_worked: minutesWorked,
        incident_member_id: userWorkLog.incidentMemberId,
        work_log_id: userWorkLog.workLogId,
      });
    }
  };

  const handleSignInStatusChange = (value: unknown) => {
    const selectedValue = (value as OptionType).value;
    if (selectedValue === 'signed_in' && userWorkLog?.workLogId) {
      createSession.mutate({
        incidentId: incidentId,
        userGuid,
        workLogId: userWorkLog.workLogId,
        signInTime: new Date().toISOString(),
      });
    } else if (
      selectedValue === 'signed_out' &&
      currentSession &&
      userWorkLog?.workLogId
    ) {
      updateSession.mutate({
        incidentId: incidentId,
        userGuid,
        workLogId: userWorkLog.workLogId,
        sessionId: currentSession.sessionId,
        signOutTime: new Date().toISOString(),
      });
    }
  };

  return (
    <Row>
      <Col>
        <PratusSelect
          label="Activation Status"
          value={activationStatus}
          options={activationStatusOptions}
          onChange={handleActivationStatusChange}
          isDisabled={
            !(
              userWorkLog?.availableActions.includes(
                WORK_LOG_ACTIONS.activate
              ) ||
              userWorkLog?.availableActions.includes(WORK_LOG_ACTIONS.decline)
            )
          }
        />
        {userWorkLog?.activatedTimestamp && (
          <div className="ms-1 mt-2">
            Activated at {formatDate(userWorkLog.activatedTimestamp)}
          </div>
        )}
      </Col>
      <Col>
        <PratusSelect
          label="Checked-In Status"
          value={checkInStatus}
          options={checkInStatusOptions}
          onChange={handleCheckInStatusChange}
          isDisabled={
            !(
              userWorkLog?.availableActions.includes(
                WORK_LOG_ACTIONS.checkIn
              ) ||
              userWorkLog?.availableActions.includes(WORK_LOG_ACTIONS.checkOut)
            )
          }
        />
        {userWorkLog?.loginTime && (
          <div className="ms-1 mt-2">
            Checked in at {formatDate(userWorkLog.loginTime)}
          </div>
        )}
      </Col>
      <Col>
        <PratusSelect
          label="Sign-In Status"
          value={signInStatus}
          options={signInStatusOptions}
          onChange={handleSignInStatusChange}
          isDisabled={
            !(
              userWorkLog?.availableActions.includes(WORK_LOG_ACTIONS.signIn) ||
              userWorkLog?.availableActions.includes(WORK_LOG_ACTIONS.signOut)
            )
          }
        />
        {currentSession?.signInTime && (
          <div className="ms-1 mt-2">
            Signed In at {formatDate(currentSession.signInTime)}
          </div>
        )}
      </Col>
    </Row>
  );
};

export default MemberStatusControls;
